import React, { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Controller, useForm } from 'react-hook-form';
import ReCAPTCHA from 'react-google-recaptcha';
import * as jwt from 'jsonwebtoken';
import { registrationValidationSchema } from '../../validation/registrationValidationSchema';
import SectionLoader from '../Loader/SectionLoader';
import FormContainer from '../FormContainer/FormContainer';
import PhoneField from '../InputFields/PhoneField';
import { registerUser } from '../../store/actions/login/loginActions';
import { useBrokerCode } from '../../util/hooks/useBrokerCode';
import {
  IDENTITY_PAGE,
  TWO_FA_VERIFY_IDENTITY_PHONE,
  TWO_FA_VERIFY_IDENTITY,
  VERIFY_CHOOSE_METHOD_PAGE,
  EMAIL_LOGIN_PAGE,
  BUY_PAGE,
} from '../../constants/pages';
import { selectIsLoadingByActionType } from '../../store/selectors/loadingSelectors';
import { REGISTER_USER_LOADING } from '../../store/actions/login/loginActionConstants';
import themeColors from '../../assets/styles/themeColors';
import {
  // selectIsReCaptchaEnabled,
  // selectPortalGatewayISOCodesListToIgnoreAreaCode,
  selectSettings,
} from '../../store/selectors/settingsSelectors';
import config from '../../config';
import {
  selectTokens,
  selectTwoFactorAuthenticationResponse,
} from '../../store/selectors/loginSelectors';
import useTrackingDetails from '../../util/hooks/useTrackingDetails';
import { useQuery } from '../../util/hooks/useQuery';
import { USER_LOGIN } from '../../constants/gtmEvents';
import useGtmHook from '../../util/hooks/useGtmHook';
import ControlledInput from '../InputFields/ControlledInput';
import { pxToRem } from '../../assets/styles/helper';
import { phoneNumberToObjectHelper } from '../../util/helpers/phoneNumberHelpers';
import { storeInSessionStorage } from '../../util/helpers/sessionStorageHelper';
import { setCookie } from '../../util/helpers/cookieHelper';
import { postTrackingDetails } from '../../store/actions/affiliate/affiliateActions';
import { LANDING_PAGE_URL, OFFER_CODE } from '../../constants/sessionStorage';
import {
  TRACKING_DETAILS_COOKIE,
  TRACKING_DETAILS_ATTRIBUTION_WINDOW_DAYS,
} from '../../constants/cookies';
import {
  getAffParam,
  getCJeventParam,
  getPromotionParam,
} from '../../util/helpers/referrerHelper';
import { variables } from '../../assets/styles/variables';
import { ALL_ZEROES_UID } from '../../constants/mockScenarios';

const RegistrationWrapper = styled.div`
  input {
    &:focus {
      border-color: ${themeColors.colorRegistration};
    }
  }

  label {
    color: ${themeColors.colorTextPrimary};
    line-height: 2.1;
    font-weight: 600;
    font-size: ${variables.typography.p.normal.fontSize};
  }

  form {
    display: grid;
    grid-template-columns: 1fr;
    gap: 32px;
    max-width: 450px;
    width: 100%;
  }
`;

const RegisterButton = styled.button`
  display: flex;
  align-items: center;
  border-radius: 4px;
  padding: 12px 24px;
  border: none;
  outline: none;
  cursor: pointer;
  background-color: ${themeColors.colorRegistration};
  width: auto;
  justify-content: center;
  font-size: 1rem;
  line-height: 1.6;
  color: ${themeColors.colorWhite};
  font-weight: 400;
  max-width: max-content;
`;
const Error = styled.div`
  dispay: flex;
  color: ${themeColors.colorRed};
`;

export const RegistrationLoginLink = styled.a`
  color: ${themeColors.colorLinkRegistration};
  font-weight: 600;
  text-decoration: underline;
`;

const BaseLayoutButtonContent = styled.div`
  display: ${(props) => (props.fullWidth ? 'grid' : 'flex')};
  grid-gap: ${(props) => (props.fullWidth ? '1rem' : 0)};
  align-items: center;
  margin-top: ${pxToRem(32)};

  @media only screen and (max-width: 992px) {
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 1rem;
  }
`;

const Registration = () => {
  const { t } = useTranslation();
  const tokens = useSelector(selectTokens);
  const dispatch = useDispatch();
  const brokerCode = useBrokerCode();
  const history = useHistory();
  const query = useQuery();
  const reCaptchaRef = React.createRef();
  const brokerSettings = useSelector(selectSettings);
  const {
    IsBroker2FAActive,
    EnableGoogleReCaptchaForQuickRegistration,
  } = brokerSettings;
  const [errorCode, setErrorCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [error, setError] = useState('');
  const referrerUrl = document.referrer;
  const landingPageUrl = window.location.href;
  const referrerAff = getAffParam();
  const referrerCJEvent = getCJeventParam();
  const referrerPromotionCode = getPromotionParam();
  const urlSearchParams = new URLSearchParams(window.location.search);
  const params = Object.fromEntries(urlSearchParams.entries());
  const { PromoCode, ReferrerEmail, RefereeEmail, email } = params;

  const { handleSubmit, control, setValue, getValues } = useForm({
    mode: 'onBlur',
    resolver: yupResolver(registrationValidationSchema),
  });

  const twoFaAuthenticationResponse = useSelector(
    selectTwoFactorAuthenticationResponse,
  );
  const { TrackingDetails } = useTrackingDetails();
  const { appGtmEvent } = useGtmHook();

  const isLoading = useSelector(
    selectIsLoadingByActionType(REGISTER_USER_LOADING),
  );

  useEffect(() => {
    const emailToFill = RefereeEmail || email;
    if (emailToFill) {
      setValue('Email', emailToFill);
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    const { aff, p, cjevent, offer, a, im_ref: imRef } = query;
    if (offer) {
      storeInSessionStorage(OFFER_CODE, offer);
    }
    storeInSessionStorage(LANDING_PAGE_URL, landingPageUrl);
    const affiliateCode = aff || referrerAff || a;
    const cjEvent = cjevent || referrerCJEvent;
    const promotionCode = p || referrerPromotionCode;
    const trackingDetails = {
      AffiliateCode: affiliateCode,
      CJEvent: cjEvent,
      PromotionCode: promotionCode,
      ReferrerUrl: imRef ? landingPageUrl : referrerUrl,
    };
    // Only persist a 90-day attribution cookie when this visit actually
    // carried partner/affiliate tracking info — an organic visitor (no
    // im_ref/aff/cjevent/promo) gets no click-ID cookie, and never has a
    // genuine earlier attribution overwritten with empty/referrer-only data.
    if (affiliateCode || cjEvent || promotionCode || imRef) {
      setCookie(
        TRACKING_DETAILS_COOKIE,
        JSON.stringify(trackingDetails),
        TRACKING_DETAILS_ATTRIBUTION_WINDOW_DAYS,
      );
    }
    setTimeout(() => {
      dispatch(postTrackingDetails({ data: trackingDetails }));
    }, 1000);
  }, []); //eslint-disable-line

  const onSubmit = async (values, { setFieldError }) => {
    if (error) return;
    let valueWithPhone = null;
    setErrorCode('');

    const {
      Iso3CountryCode,
      AreaCode,
      LocalNumber,
    } = phoneNumberToObjectHelper(values.PhoneNumber);

    valueWithPhone = {
      ...values,
      PhoneNumber:
        Iso3CountryCode && AreaCode && LocalNumber
          ? {
              Iso3CountryCode,
              AreaCode,
              LocalNumber,
              PhoneNumberUid: ALL_ZEROES_UID,
              EntityPhonenumberType: 'Primary',
            }
          : null,
    };
    let token = null;
    if (EnableGoogleReCaptchaForQuickRegistration) {
      token = await reCaptchaRef.current.executeAsync();
      reCaptchaRef.current.reset();
    }
    const handleGtm = () => {
      // gtmEvent(APPLICATION_STARTED);
      appGtmEvent(`app.${USER_LOGIN}`);
    };
    const newData = token
      ? {
          ...valueWithPhone,
          BrokerCode: brokerCode,
          RecaptchaToken: token,
        }
      : {
          ...valueWithPhone,
          BrokerCode: brokerCode,
        };
    let NewTrackingDetails = TrackingDetails;
    if (PromoCode) {
      NewTrackingDetails = {
        ...NewTrackingDetails,
        ReferralCode: PromoCode,
      };
    }
    if (ReferrerEmail) {
      NewTrackingDetails = {
        ...NewTrackingDetails,
        ReferrerEmail,
      };
    }
    dispatch(
      registerUser({
        BasicAccountRequest: { ...newData },
        TrackingDetails: NewTrackingDetails,
        setFieldError,
        setErrorCode,
        setErrorMessage,
        handleGtm,
      }),
    );
  };

  useEffect(() => {
    function redirectClient() {
      if (!tokens.RefreshToken && !tokens.JwtToken) {
        return;
      }

      if (IsBroker2FAActive) {
        const authenticationMethodType = jwt.decode(tokens.JwtToken)
          ?.AuthenticationMethodType;

        if (tokens.JwtToken) {
          if (authenticationMethodType) {
            return history.push({
              pathname: BUY_PAGE,
              state: {
                from: history.location.pathname,
              },
            });
          }
          const { isRequired2FA } = jwt.decode(tokens.JwtToken);
          if (isRequired2FA === 'True') {
            return history.push({
              pathname: VERIFY_CHOOSE_METHOD_PAGE,
              state: {
                from: history.location.pathname,
              },
            });
          }
        }
      }

      if (tokens.RefreshToken) {
        if (IsBroker2FAActive && twoFaAuthenticationResponse) {
          if (twoFaAuthenticationResponse.AuthenticationMethodType === 2) {
            return history.replace(TWO_FA_VERIFY_IDENTITY_PHONE);
          }
          return history.replace(TWO_FA_VERIFY_IDENTITY);
        }
        return history.replace(IDENTITY_PAGE);
      }

      return history.push({
        pathname: BUY_PAGE,
        state: {
          from: history.location.pathname,
        },
      });
    }
    if (tokens) {
      redirectClient();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokens]);

  const displayError = () => {
    if (errorCode === '') {
      return;
    }
    if (errorCode === 'UsernameAlreadyExists') {
      return (
        <Error>
          {errorMessage}
          <RegistrationLoginLink
            onClick={() => {
              history.push(EMAIL_LOGIN_PAGE);
            }}
          >
            {t('registration.signIn')}
          </RegistrationLoginLink>
          {t('registration.instead')}
        </Error>
      );
    }
    return <Error>{errorMessage}</Error>;
  };

  const handleExpired = () =>
    setErrorMessage(t('apiErrors.ExpiredRecaptchaToken'));

  const handleErrored = () =>
    setErrorMessage(t('apiErrors.InvalidRecaptchaToken'));

  return (
    <RegistrationWrapper>
      <SectionLoader isLoading={isLoading}>
        <FormContainer>
          <FormContainer hasError={errorCode !== ''}>
            <Controller
              control={control}
              rules={{
                required: true,
              }}
              render={({ field, fieldState }) => (
                <ControlledInput
                  {...field}
                  {...fieldState}
                  label={t('quickRegistration.labels.email')}
                  autoComplete="email"
                  data-cy="input-email"
                  autoFocus
                />
              )}
              name="Email"
            />
            {displayError()}
          </FormContainer>
          <Controller
            control={control}
            render={({ field, fieldState }) => (
              <ControlledInput
                {...field}
                {...fieldState}
                type="password"
                label={t('quickRegistration.labels.password')}
                autoComplete="new-password"
                data-cy="input-password"
              />
            )}
            name="Password"
          />
          <Controller
            control={control}
            render={({ field, fieldState }) => (
              <ControlledInput
                {...field}
                {...fieldState}
                label={t('quickRegistration.labels.firstName')}
                autoComplete="given-name"
                data-cy="input-first-name"
              />
            )}
            name="FirstName"
          />
          <Controller
            control={control}
            render={({ field, fieldState }) => (
              <ControlledInput
                {...field}
                {...fieldState}
                label={t('quickRegistration.labels.lastName')}
                autoComplete="family-name"
                data-cy="input-last-name"
              />
            )}
            name="LastName"
          />
          <Controller
            control={control}
            render={({ field, fieldState }) => (
              <PhoneField
                {...field}
                {...fieldState}
                optional={false}
                name="PhoneNumber"
                label={t('quickRegistration.labels.phoneNumber')}
                setValue={setValue}
                setError={setError}
                defaultValue={getValues('PhoneNumber')}
                dataCy="input-phone"
                error={fieldState?.error?.message}
              />
            )}
            name="PhoneNumber"
          />
          {PromoCode && (
            <Controller
              control={control}
              render={({ field, fieldState }) => (
                <ControlledInput
                  {...field}
                  {...fieldState}
                  label={t('quickRegistration.labels.promoCode')}
                  autoComplete="promo-code"
                  data-cy="input-promo-code"
                  value={PromoCode}
                  disabled
                />
              )}
            />
          )}
        </FormContainer>
        <BaseLayoutButtonContent>
          <RegisterButton
            type="submit"
            data-cy="button-continue"
            onClick={handleSubmit(onSubmit)}
          >
            {t('common.continue')}
          </RegisterButton>
        </BaseLayoutButtonContent>
        {EnableGoogleReCaptchaForQuickRegistration && (
          <ReCAPTCHA
            ref={reCaptchaRef}
            sitekey={config.recaptcha.siteKey}
            size="invisible"
            onExpired={handleExpired}
            onErrored={handleErrored}
          />
        )}
      </SectionLoader>
    </RegistrationWrapper>
  );
};

Registration.propTypes = {
  history: PropTypes.shape({
    replace: PropTypes.func,
  }),
};

export default Registration;
